import { useEffect, useMemo, useState } from 'react'

import { ImportIcon, Play, RotateCcw } from 'lucide-react'

import { extractKeys } from '@fortem-monorepo/js-utilities'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { CodeBlock } from '@fortem-monorepo/react-ui'
import { SubmitButton } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { Card, CardContent } from '@fortem-monorepo/react-ui'
import { Form } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'

import { Page } from '@/components/Page'
import { ImportCommandModal } from '@/components/dialogs/ImportCommandModal'
import { MONITORING } from '@/constants/links'
import {
  getCommandFromFormValues,
  getFormValuesFromCommand,
  keys,
} from '@/pages/monitoring/custom-jobs/command-helpers'
import { CustomJobFormFields } from '@/pages/monitoring/custom-jobs/components/CustomJobFormFields'
import {
  CustomJobFormType,
  customJobSchema,
} from '@/pages/monitoring/custom-jobs/components/CustomJobFormSchema'
import { allScheduledTasks } from '@/pages/monitoring/task-data'

const newFormValues: Partial<CustomJobFormType> = {
  recipients: [{ matcher: 'contains', value: '' }],
  sender: [{ matcher: 'contains', value: '' }],
  subject: [{ matcher: 'contains', value: '' }],
}

export interface CustomJobPageProps {}

export const CustomJobPage: React.FC<CustomJobPageProps> = ({}) => {
  const [isImportingCommand, setIsImportingCommand] = useState(false)
  const [showCustomArgs, setShowCustomArgs] = useState(false)
  const [command, setCommand] = useState('')
  const [importError, setImportError] = useState('')

  const form = useFormWithSchema(
    {
      defaultValues: newFormValues,
      mode: 'all',
    },
    customJobSchema,
  )

  const {
    handleSubmit,
    trigger,
    watch,
    formState: { errors },
  } = form

  const values = watch()

  /**
   * Format the command in a more human readable format
   * Important to represent HTML Input values as raw strings here
   * as it will be used to copy to clipboard
   */
  const formattedCommand = useMemo(() => {
    // return command
    if (command.includes('Invalid')) {
      return command
    }

    const keyValues = extractKeys(command, keys)

    let _str = ''

    Object.entries(keyValues).forEach(([key, values]) => {
      values.forEach((value) => {
        _str += `--${key} "${value}"\n`
      })
    })

    return _str
  }, [command])

  const FormattedError = useMemo(
    () => () => {
      if (errors) {
        const test = customJobSchema.safeParse(values)
        if (!test.success) {
          return renderFormErrors(test.error.message)
        }
      }
      return null
    },
    [values],
  )

  const onSubmit = handleSubmit(async () => {})

  // ----- EFFECTS

  useEffect(() => {
    const test = customJobSchema.safeParse(values)

    if (!test.success) {
      setCommand('Invalid or missing form values.')
    }

    if (test.success) {
      setCommand(getCommandFromFormValues(values))
    }
  }, [values])

  useEffect(() => {
    trigger()
  }, [trigger])

  return (
    <Page metadata={{ title: 'Custom Jobs' }} permissions={MONITORING.CUSTOM_JOBS.permissions}>
      <Form {...form}>
        <ViewContainer>
          <div className="flex gap-6">
            <form className={'flex-1 h-full overflow-hidden'} onSubmit={onSubmit}>
              <div className="flex flex-col gap-4">
                <Card variant={'simple'}>
                  <CardContent>
                    <CustomJobFormFields
                      taskOptions={allScheduledTasks.map((task) => ({
                        label: task.taskName,
                        value: task.taskName,
                      }))}
                      showCustomArgs={showCustomArgs}
                      toggleShowCustomArgs={() => setShowCustomArgs((prev) => !prev)}
                    />
                  </CardContent>
                </Card>
                <Card variant={'simple'}>
                  <CardContent className={'space-y-3'}>
                    <FormItem>
                      <div className={'flex justify-between gap-2'}>
                        <Label>Command</Label>
                        <div className="flex items-center gap-2">
                          <SubmitButton
                            size="sm"
                            variant={'default'}
                            disabled={!form.formState.isValid}
                          >
                            <Play />
                            Run
                          </SubmitButton>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                size="sm"
                                variant={'outline'}
                                onClick={() => {
                                  setImportError('')
                                  setIsImportingCommand(true)
                                }}
                              >
                                <ImportIcon />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Import Command</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                size="sm"
                                variant={'outline'}
                                onClick={() => {
                                  form.reset(newFormValues)
                                }}
                              >
                                <RotateCcw />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Reset Fields</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      <CodeBlock className={'mt-3 whitespace-pre-line'}>
                        {formattedCommand}
                      </CodeBlock>
                    </FormItem>
                    <div className="flex items-center gap-2"></div>
                  </CardContent>
                </Card>
              </div>
            </form>
            <div className="relative flex bg-neutral-100 rounded-md flex-1 min-h-full">
              <div className="absolute top-0 left-0 w-full h-full overflow-auto p-6">
                <pre className={'text-xs'}>
                  <FormattedError />
                </pre>
              </div>
            </div>
          </div>
        </ViewContainer>
      </Form>
      <ImportCommandModal
        onImport={(command) => {
          // Convert command to form data
          const values = getFormValuesFromCommand(command)

          const test = customJobSchema.safeParse(values)

          if (!test.success) {
            setImportError(test.error.message)
            return
          }

          setCommand(command)
          form.reset(values)
          setIsImportingCommand(false)
        }}
        open={isImportingCommand}
        onOpenChange={setIsImportingCommand}
      >
        {importError && (
          <div className={'w-full border border-destructive p-2 mt-3 rounded-md'}>
            {renderFormErrors(importError)}
          </div>
        )}
      </ImportCommandModal>
    </Page>
  )
}

const renderFormErrors = (errors: string) => {
  return JSON.parse(errors).map((info: any, index: number) => {
    return (
      <div className={'mb-1'} key={index}>
        <span className={'text-xs font-medium'}>{info.path.join('.')}</span>
        <span className={'pl-2 whitespace-break-spaces text-xs'}>{info.message}</span>
      </div>
    )
  })
}
